// =====================================================================
// Assessment Center — Student Panel
// Exam Page — full-screen, distraction-free exam runner. Deliberately
// does not use StudentLayout's sidebar, matching the existing
// AviationHub exam-runner chrome: navy top bar with timer/finish,
// question status row, question card, bottom nav strip with a
// question-number rail.
//
// "Flag for review" and font-size are session-only UI state — the
// schema has no column for either, and it isn't changed just to
// persist UI chrome without a real product reason to.
// No grading happens here: MCQ selection and essay text are saved as
// given, is_correct/points stay null until a future grading phase.
// =====================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spinner } from "../../../shared/components/ui";
import {
  getAttemptRunner,
  saveAnswer,
  submitAttempt,
  type AttemptRunner,
  type RunnerQuestion,
} from "../lib/mock-api";

type SaveState = "idle" | "saving" | "saved";

interface FlatQuestion extends RunnerQuestion {
  sectionTitle: string;
  sectionType: RunnerQuestion["type"];
}

const FONT_SCALES = ["text-base", "text-lg", "text-xl"];

export default function ExamPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();

  const [runner, setRunner] = useState<AttemptRunner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [answers, setAnswers] = useState<
    Record<string, { selected_answer_id: string | null; essay_answer_text: string | null }>
  >({});
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({});
  const [flagged, setFlagged] = useState<Set<string>>(new Set());
  const [index, setIndex] = useState(0);
  const [fontScale, setFontScale] = useState(1);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [confirmingFinish, setConfirmingFinish] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!attemptId) return;
    getAttemptRunner(attemptId)
      .then((data) => {
        setRunner(data);
        setAnswers(data.answers);
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Couldn't load this exam.");
        setLoading(false);
      });
  }, [attemptId]);

  const flatQuestions: FlatQuestion[] = useMemo(() => {
    if (!runner) return [];
    return runner.sections.flatMap((s) =>
      s.questions.map((q) => ({ ...q, sectionTitle: s.title, sectionType: s.section_type }))
    );
  }, [runner]);

  // Countdown timer, derived from started_at + duration_minutes.
  useEffect(() => {
    if (!runner) return;
    const deadline =
      new Date(runner.attempt.started_at ?? runner.attempt.created_at).getTime() +
      runner.exam.duration_minutes * 60_000;
    const tick = () => {
      const remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) handleSubmit(true);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runner]);

  function formatTime(total: number) {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  function queueSave(examQuestionId: string, payload: { selected_answer_id?: string | null; essay_answer_text?: string | null }) {
    if (!attemptId) return;
    setSaveStates((prev) => ({ ...prev, [examQuestionId]: "saving" }));
    if (debounceTimers.current[examQuestionId]) {
      clearTimeout(debounceTimers.current[examQuestionId]);
    }
    debounceTimers.current[examQuestionId] = setTimeout(async () => {
      try {
        await saveAnswer(attemptId, examQuestionId, payload);
        setSaveStates((prev) => ({ ...prev, [examQuestionId]: "saved" }));
      } catch {
        setSaveStates((prev) => ({ ...prev, [examQuestionId]: "idle" }));
      }
    }, 600);
  }

  function selectOption(q: FlatQuestion, optionId: string) {
    setAnswers((prev) => ({ ...prev, [q.exam_question_id]: { selected_answer_id: optionId, essay_answer_text: null } }));
    queueSave(q.exam_question_id, { selected_answer_id: optionId });
    if (autoAdvance) setTimeout(() => goTo(index + 1), 250);
  }

  function updateEssay(q: FlatQuestion, text: string) {
    setAnswers((prev) => ({ ...prev, [q.exam_question_id]: { selected_answer_id: null, essay_answer_text: text } }));
    queueSave(q.exam_question_id, { essay_answer_text: text });
  }

  function toggleFlag(examQuestionId: string) {
    setFlagged((prev) => {
      const next = new Set(prev);
      next.has(examQuestionId) ? next.delete(examQuestionId) : next.add(examQuestionId);
      return next;
    });
  }

  function goTo(i: number) {
    if (i < 0 || i >= flatQuestions.length) return;
    setIndex(i);
  }

  function isAnswered(q: RunnerQuestion) {
    const a = answers[q.exam_question_id];
    return Boolean(a?.selected_answer_id || (a?.essay_answer_text && a.essay_answer_text.trim()));
  }

  async function handleSubmit(auto = false) {
    if (!attemptId) return;
    setSubmitting(true);
    try {
      await submitAttempt(attemptId);
      navigate(`/student/assessment-center/results/${attemptId}`, {
        state: auto ? { autoSubmitted: true } : undefined,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't submit the exam.");
      setSubmitting(false);
      setConfirmingFinish(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 bg-navy-50/40 text-slate-500">
        <Spinner /> Loading exam…
      </div>
    );
  }

  if (error || !runner) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-navy-50/40 px-4 text-center">
        <p className="text-sm text-red-600">{error ?? "This exam couldn't be loaded."}</p>
        <Button variant="secondary" onClick={() => navigate("/student/assessment-center/my-exams")}>
          Back to My Exams
        </Button>
      </div>
    );
  }

  const current = flatQuestions[index];
  const answeredCount = flatQuestions.filter(isAnswered).length;
  const saveState = current ? saveStates[current.exam_question_id] ?? "idle" : "idle";

  return (
    <div className="flex min-h-screen flex-col bg-navy-50/40 font-body text-slate-800">
      {/* Top bar */}
      <header className="flex flex-wrap items-center justify-between gap-3 bg-navy-700 px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 font-display text-sm font-bold text-white">
            AH
          </div>
          <span className="font-display text-sm font-semibold uppercase tracking-wide text-white">
            {runner.exam.title}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-navy-800 px-4 py-1.5 text-center">
            <p className="text-[10px] uppercase tracking-wide text-navy-200">Time left</p>
            <p className="font-display text-lg font-semibold text-amber-300">
              {secondsLeft !== null ? formatTime(secondsLeft) : "--:--"}
            </p>
          </div>
          <button
            onClick={() => setFontScale((s) => Math.max(0, s - 1))}
            className="rounded-lg bg-navy-800 px-3 py-2 text-sm font-semibold text-white hover:bg-navy-900"
            aria-label="Decrease text size"
          >
            A-
          </button>
          <button
            onClick={() => setFontScale((s) => Math.min(FONT_SCALES.length - 1, s + 1))}
            className="rounded-lg bg-navy-800 px-3 py-2 text-sm font-semibold text-white hover:bg-navy-900"
            aria-label="Increase text size"
          >
            A+
          </button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setConfirmingFinish(true)}
          >
            ✓ Finish
          </Button>
        </div>
      </header>

      {/* Status row */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy-700 font-display text-sm font-bold text-white">
            Q{index + 1}
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Status</p>
            <p className="text-sm text-slate-600">
              {answeredCount} / {flatQuestions.length} answered
              {saveState === "saving" && <span className="ml-2 text-xs text-slate-400">Saving…</span>}
              {saveState === "saved" && <span className="ml-2 text-xs text-emerald-600">Saved</span>}
            </p>
          </div>
        </div>
        <button
          onClick={() => current && toggleFlag(current.exam_question_id)}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium uppercase tracking-wide ${
            current && flagged.has(current.exam_question_id)
              ? "border-amber-300 bg-amber-50 text-amber-700"
              : "border-border-soft text-slate-500 hover:bg-navy-50"
          }`}
        >
          {current && flagged.has(current.exam_question_id) ? "★" : "☆"} Flag for review
        </button>
      </div>

      {/* Question */}
      <main className="flex-1 px-4 pb-28 sm:px-6">
        {current && (
          <div className="mx-auto max-w-3xl">
            <p className="mb-2 text-xs uppercase tracking-wide text-navy-500">{current.sectionTitle}</p>

            <div className={`rounded-card border-l-4 border-navy-700 bg-white p-5 shadow-soft ${FONT_SCALES[fontScale]}`}>
              <p className="text-slate-800">{current.prompt}</p>
            </div>

            <div className="mt-4 space-y-3">
              {current.type === "multiple_choice" &&
                current.options?.map((opt, i) => {
                  const letter = String.fromCharCode(65 + i);
                  const selected = answers[current.exam_question_id]?.selected_answer_id === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => selectOption(current, opt.id)}
                      className={`flex w-full items-center gap-4 rounded-card border bg-white p-4 text-left shadow-soft transition-colors ${
                        selected ? "border-navy-700 ring-1 ring-navy-300" : "border-border-soft hover:bg-navy-50/40"
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-display text-sm font-semibold ${
                          selected ? "bg-navy-700 text-white" : "bg-navy-100 text-navy-700"
                        }`}
                      >
                        {letter}
                      </span>
                      <span className={`text-slate-700 ${FONT_SCALES[fontScale]}`}>{opt.answer_text}</span>
                    </button>
                  );
                })}

              {current.type === "essay" && (
                <div className="rounded-card border border-border-soft bg-white p-4 shadow-soft">
                  <textarea
                    value={answers[current.exam_question_id]?.essay_answer_text ?? ""}
                    onChange={(e) => updateEssay(current, e.target.value)}
                    placeholder="Type your answer…"
                    className={`min-h-[220px] w-full resize-y border-none p-0 text-slate-700 focus:outline-none ${FONT_SCALES[fontScale]}`}
                  />
                  <div className="mt-2 flex justify-between text-xs text-slate-400">
                    <span>
                      {(answers[current.exam_question_id]?.essay_answer_text ?? "").trim().split(/\s+/).filter(Boolean).length}{" "}
                      words
                      {current.min_words || current.max_words
                        ? ` · target ${current.min_words ?? 0}–${current.max_words ?? "∞"}`
                        : ""}
                    </span>
                    <span>{current.points} pts</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Bottom nav */}
      <footer className="fixed inset-x-0 bottom-0 border-t border-border-soft bg-navy-700">
        <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Button variant="secondary" disabled={index === 0} onClick={() => goTo(index - 1)}>
            ‹ Prev
          </Button>
          <label className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-navy-700">
            <input
              type="checkbox"
              checked={autoAdvance}
              onChange={(e) => setAutoAdvance(e.target.checked)}
              className="h-4 w-4 accent-navy-700"
            />
            Auto
          </label>
          <Button
            className="bg-white text-navy-700 hover:bg-navy-50"
            disabled={index === flatQuestions.length - 1}
            onClick={() => goTo(index + 1)}
          >
            Next ›
          </Button>
        </div>
        <div className="flex gap-1.5 overflow-x-auto border-t border-navy-600 px-4 py-2 sm:px-6">
          {flatQuestions.map((q, i) => {
            const answered = isAnswered(q);
            const isFlagged = flagged.has(q.exam_question_id);
            const isCurrent = i === index;
            return (
              <button
                key={q.exam_question_id}
                onClick={() => goTo(i)}
                className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-display text-xs font-semibold ${
                  isCurrent
                    ? "bg-white text-navy-700 ring-2 ring-white"
                    : answered
                      ? "bg-navy-500 text-white"
                      : "border border-navy-500 text-navy-100"
                }`}
              >
                {String(i + 1).padStart(2, "0")}
                {isFlagged && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-400" />
                )}
              </button>
            );
          })}
        </div>
      </footer>

      {confirmingFinish && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 px-4"
          onMouseDown={(e) => e.target === e.currentTarget && !submitting && setConfirmingFinish(false)}
        >
          <div className="w-full max-w-sm rounded-card border border-border-soft bg-white p-5 shadow-soft">
            <h3 className="font-display text-lg font-semibold text-navy-900">Finish exam?</h3>
            <p className="mt-2 text-sm text-slate-600">
              You've answered {answeredCount} of {flatQuestions.length} questions. Once submitted, you
              can't change your answers.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" disabled={submitting} onClick={() => setConfirmingFinish(false)}>
                Keep working
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={submitting}
                onClick={() => handleSubmit(false)}
              >
                {submitting ? <Spinner /> : null} Submit exam
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
